import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, FileText, CheckCircle, AlertCircle, 
  Building, Calendar, Euro, Zap, Shield, Wifi, Wrench,
  Loader, Save, Edit
} from 'lucide-react';
import { masterSupabase as supabase } from '../../lib/supabase-master.js';

// Contract Categories
const contractCategories = [
  { id: 'energy', name: 'Energie', icon: Zap, color: 'yellow' },
  { id: 'insurance', name: 'Versicherung', icon: Shield, color: 'blue' },
  { id: 'telecom', name: 'Telekommunikation', icon: Wifi, color: 'purple' },
  { id: 'maintenance', name: 'Wartung', icon: Wrench, color: 'green' },
  { id: 'other', name: 'Sonstiges', icon: FileText, color: 'gray' }
];

const ContractReviewModal = ({ 
  isOpen, 
  onClose, 
  contractData = {}, 
  document = null,
  onContractCreate 
}) => {
  // State Management
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    provider: '',
    category: 'other',
    property_id: null,
    monthly_cost: 0,
    start_date: '',
    end_date: '',
    cancellation_period: 30,
    cancellation_notice_period: 30,
    auto_renew: false,
    status: 'active'
  });

  // Load properties and owners
  useEffect(() => {
    if (isOpen) {
      loadPropertiesAndOwners();
      
      // Pre-fill form with detected contract data
      if (contractData && contractData.provider) {
        setFormData(prev => ({
          ...prev,
          name: `${contractData.provider} - ${contractData.contract_type || 'Vertrag'}`,
          provider: contractData.provider || '',
          category: contractData.contract_type || 'other',
          monthly_cost: contractData.monthly_cost || 0,
          start_date: contractData.start_date || '',
          end_date: contractData.end_date || '',
          cancellation_period: contractData.cancellation_period || 30,
          auto_renew: contractData.auto_renewal || false
        }));
      }
    }
  }, [isOpen, contractData]);

  const loadPropertiesAndOwners = async () => {
    try {
      // Load properties
      const { data: propertiesData, error: propError } = await supabase
        .from('properties')
        .select('id, name, owner_id')
        .order('name');
      
      if (propError) throw propError;
      setProperties(propertiesData || []);

      // Load owners
      const { data: ownersData, error: ownerError } = await supabase
        .from('owners')
        .select('id, name')
        .order('name');
      
      if (ownerError) throw ownerError;
      setOwners(ownersData || []);
      
    } catch (error) {
      console.error('Error loading properties/owners:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveContract = async () => {
    // Validation
    if (!formData.name || !formData.provider) {
      alert('Bitte Name und Anbieter ausfüllen');
      return;
    }

    if (!formData.property_id) {
      alert('Bitte ein Objekt auswählen');
      return;
    }

    setSaving(true);

    try {
      // 1. Create contract in database
      const { data: contract, error: contractError } = await supabase
        .from('contracts')
        .insert({
          name: formData.name,
          provider: formData.provider,
          category: formData.category,
          property_id: formData.property_id,
          monthly_cost: parseFloat(formData.monthly_cost) || 0,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          cancellation_period: parseInt(formData.cancellation_period) || 30,
          cancellation_notice_period: parseInt(formData.cancellation_notice_period) || 30,
          auto_renew: formData.auto_renew,
          status: formData.status
        })
        .select()
        .single();

      if (contractError) throw contractError;

      console.log('✅ Contract created:', contract);

      // 2. Link contract to document (if document exists)
      if (document && document.id) {
        const { error: linkError } = await supabase
          .from('contract_documents')
          .insert({
            contract_id: contract.id,
            document_id: document.id
          });

        if (linkError) {
          console.warn('⚠️ Could not link contract to document:', linkError);
        } else {
          console.log('✅ Contract linked to document');
        }

        // 3. Update document category
        await supabase
          .from('documents')
          .update({ category: 'Vertrag' })
          .eq('id', document.id);
      }

      // 4. Callback to parent
      if (onContractCreate) {
        onContractCreate(contract);
      }

      // Close modal
      onClose();

    } catch (error) {
      console.error('❌ Error creating contract:', error);
      alert(`Fehler beim Erstellen des Vertrags: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const selectedCategory = contractCategories.find(c => c.id === formData.category);
  const CategoryIcon = selectedCategory?.icon || FileText;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <CategoryIcon className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Vertrag prüfen & erstellen</h2>
              <p className="text-blue-100 text-sm">
                {contractData.confidence 
                  ? `KI-Erkennung: ${Math.round(contractData.confidence * 100)}% Sicherheit`
                  : 'Manuell erfassen'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vertragsname *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="z.B. Stadtwerke Strom 2024"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anbieter *
              </label>
              <input
                type="text"
                value={formData.provider}
                onChange={(e) => handleInputChange('provider', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="z.B. Stadtwerke Mannheim"
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kategorie
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {contractCategories.map(cat => {
                const Icon = cat.icon;
                const isSelected = formData.category === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleInputChange('category', cat.id)}
                    className={`p-3 rounded-lg border-2 transition flex flex-col items-center space-y-1 ${
                      isSelected 
                        ? `border-${cat.color}-500 bg-${cat.color}-50` 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${isSelected ? `text-${cat.color}-600` : 'text-gray-400'}`} />
                    <span className={`text-xs font-medium ${isSelected ? `text-${cat.color}-700` : 'text-gray-600'}`}>
                      {cat.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Property Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Eigentümer
              </label>
              <select
                value={properties.find(p => p.id === formData.property_id)?.owner_id || ''}
                onChange={(e) => {
                  // When owner changes, reset property selection
                  handleInputChange('property_id', null);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Alle Eigentümer</option>
                {owners.map(owner => (
                  <option key={owner.id} value={owner.id}>{owner.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Objekt *
              </label>
              <select
                value={formData.property_id || ''}
                onChange={(e) => handleInputChange('property_id', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Bitte wählen...</option>
                {properties.map(property => (
                  <option key={property.id} value={property.id}>{property.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Costs & Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monatliche Kosten (€)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.monthly_cost}
                onChange={(e) => handleInputChange('monthly_cost', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vertragsbeginn
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vertragsende
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Cancellation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kündigungsfrist (Tage)
              </label>
              <input
                type="number"
                value={formData.cancellation_notice_period}
                onChange={(e) => handleInputChange('cancellation_notice_period', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="30"
              />
            </div>

            <div className="flex items-center space-x-2 pt-7">
              <input
                type="checkbox"
                id="auto_renew"
                checked={formData.auto_renew}
                onChange={(e) => handleInputChange('auto_renew', e.target.checked)}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="auto_renew" className="text-sm font-medium text-gray-700">
                Automatische Verlängerung
              </label>
            </div>
          </div>

          {/* Document Info */}
          {document && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">Verknüpftes Dokument</p>
                  <p className="text-sm text-blue-700">{document.file_name || document.filename}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end space-x-3 border-t">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSaveContract}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center space-x-2"
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Speichern...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Vertrag erstellen</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractReviewModal;

